import { range } from 'lodash';
import { convertDurationForFE } from './convertDuration';
import { MINUTE_OF_A_BLOCK } from './getTimeBlockFromTImeString';

export const TOKEN_EXPIRED_MESSAGE = 'The token is expired';

export const APPOINTMENT_STATUS = {
  NEW: { KEY: 'new', LABEL: 'New' },
  ACCEPTED: { KEY: 'accepted', LABEL: 'Accept' },
  MISSED: { KEY: 'missed', LABEL: 'Miss' },
  COMPLETED: { KEY: 'completed', LABEL: 'Complete' },
  CANCELLED: { KEY: 'cancelled', LABEL: 'Cancel' },
  ERROR: { KEY: 'error', LABEL: 'Error' },
  ALL: { KEY: 'all', LABEL: 'All' },
};

export const ERROR_REASON = {
  DUPLICATE_BOOKING: 'duplicate_booking',
  PARTIAL_PATIENT_MATCH: 'partial_patient_match',
  GENERAL_ERROR: 'general_error',
  OUT_OF_OFFICE_HOURS: 'out_of_office_hours',
  OUT_OF_PRACTITIONER_HOURS: 'out_of_practitioner_hours',
  PATIENT_NEED_ADD_TO_FAMILY: 'patient_need_add_to_family',
  WRITE_FAIL: 'write_fail',
  UNFOUNDED_AVAILABLE_PRACTITIONER: 'unfounded_available_practitioner',
  WARNING_PENDING: 'warning_pending',
  INACTIVE_PATIENT: 'inactive_patient',
  EXISTING_BOOKING_BY_PATIENT: 'existing_booking_by_patient',
};

export const FIRSTIN_SYSTEM = 'firstin';

export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

export const STATUS = { ACCEPT: 'Accept' as const, REJECT: 'Reject' as const };

export type ErrorAppointmentStatusType =
  | 'success'
  | 'error'
  | 'duplicate'
  | 'already_resolved'
  | 'timeslot_not_available'
  | 'out_of_office_hours'
  | 'out_of_practitioner_hours'
  | 'general_error'
  | 'unfounded_available_practitioner'
  | 'null_operatory';

export const ERROR_APPOINTMENT_STATUS = {
  SUCCESS: 'success' as const,
  ERROR: 'error' as const,
  DUPLICATE: 'duplicate' as const,
  ALREADY_RESOLVED: 'already_resolved' as const,
  TIMESLOT_NOT_AVAILABLE: 'timeslot_not_available' as const,
  OUT_OF_OFFICE_HOURS: 'out_of_office_hours' as const,
  OUT_OF_PRACTITIONER_HOURS: 'out_of_practitioner_hours' as const,
  GENERAL_ERROR: 'general_error' as const,
  UNFOUNDED_AVAILABLE_PRACTITIONER: 'unfounded_available_practitioner' as const,
  NULL_OPERATORY: 'null_operatory' as const,
};

export const ERROR_RESPONSE_SERVER = {
  ALREADY_RESOLVED_MESSAGE: 'Appointment is resolved successfully' as const,
  TIMESLOT_NOT_AVAILABLE_MESSAGE: 'Time slot is not available' as const,
  DUPLICATE_BOOKING: 'duplicate_booking' as const,
  APPOINTMENT_NOT_SYNC_WITH_PMS: 'Appointment is not sync with PMS' as const,
};

export const SUPPORT_EMAIL = 'hello@firstin.io';

export const MAX_CLINIC_API_KEYS = 5;

export const GENERIC_ERROR_MESSAGE = 'An error has occurred. Please try again.';

export const ROLE = {
  GLOBAL_ADMIN: 'global_admin',
  GROUP_ADMIN: 'group_admin',
  PRACTICE_ADMIN: 'practice_admin',
  PRACTICE_USER: 'practice_user',
};

export const CLINIC_USER_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  PENDING: 'pending',
};

export const SYNC_STATUS = {
  SUCCESS: 'success',
  FAIL: 'fail',
  IN_PROGRESS: 'in_progress',
};

export const SCHEDULE_TYPE = {
  GOOGLE: 'googleWorkingHour',
  DEFAULT: 'defaultWorkingHour',
};

export const WORKING_HOUR_SYSTEM = {
  GOOGLE: 'google',
  FIRSTIN: 'firstin',
};

export const DEFAULT_WORKING_HOUR = {
  START: '08:00:00',
  END: '17:00:00',
};

export const TIME_GRID_WORKING_HOUR = {
  START: '01:00:00',
  END: '24:00:00',
};

export const PRACTITIONER_FILTER_OPTIONS = {
  ALL: 'All',
  AVAILABLE: 'Available',
  UNAVAILABLE: 'Unavailable',
};

export const PRACTICE_FILTER_OPTIONS = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

export const WORKING_HOUR_EXCEPTION = {
  CLOSED: 'Closed',
  HOLIDAY: 'Holiday',
};

export const CUSTOM_HOURS = 'Custom Hours';

export const DAY_OFF = 'Day Off';

export const OFFICE_CLOSED = 'Office Closed';

export const NEXT_23_MONTH = '23';

export const UNAVAILABLE = 'Unavailable';

export const DEFAULT_COUNTRY_CODE = 'ca';

export const PMS_TYPE = {
  DENTRIX: 'dentrix',
  FIRST_IN: 'firstin',
  NEX_HEALTH: 'nexhealth',
};

export const EXAM_CLEANING_DURATION_OPTIONS = range(
  2,
  155 / MINUTE_OF_A_BLOCK, // 155 minutes
  2
).map((value) => convertDurationForFE(value));

export const DURATION_OPTIONS = range(2, 190 / MINUTE_OF_A_BLOCK, 2).map(
  (value) => convertDurationForFE(value)
);

export const COLOR_CODES = [
  '#B01BAE',
  '#4D97E1',
  '#1A9901',
  '#867BEB',
  '#47B2AD',
  '#EAA51F',
  '#DC5C89',
  '#51169D',
  '#FF544A',
  '#32ABCA',
  '#D2B303',
  '#22668D',
  '#FF3FA4',
  '#A4907C',
  '#00CFFD',
  '#C87A9E',
  '#57375D',
  '#DF7358',
  '#57375D',
  '#5C8374',
  '#E9B384',
  '#A2C579',
  '#6C9BCF',
  '#A96D4B',
  '#654E92',
  '#016A70',
  '#9A3B3B',
  '#51698D',
  '#73AC97',
  '#C78AFE',
  '#E85D04',
  '#2A9D8F',
  '#8B5CF6',
  '#EC4899',
  '#06B6D4',
  '#F59E0B',
  '#10B981',
  '#6366F1',
  '#EF4444',
  '#A855F7',
  '#FB923C',
  '#14B8A6',
  '#D946EF',
  '#0EA5E9',
  '#84CC16',
  '#F43F5E',
  '#F4A261',
  '#264653',
  '#22D3EE',
  '#F97316',
  '#7C3AED',
  '#059669',
  '#D97706',
  '#0891B2',
  '#BE123C',
  '#4F46E5',
  '#65A30D',
  '#EA580C',
  '#9333EA',
  '#0D9488',
  '#C026D3',
  '#2563EB',
  '#DB2777',
  '#15803D',
  '#B45309',
  '#1D4ED8',
  '#9D174D',
  '#047857',
  '#DC2626',
  '#4338CA',
];

export const BACKGROUND_COLOR_CODES = [
  '#FFF6FF',
  '#EEFBFB',
  '#F7FFF2',
  '#F9F9FF',
  '#EDFCFC',
  '#FFF7E9',
  '#FFF3F7',
  '#F7F1FF',
  '#FFF3F7',
  '#F2FCFF',
  '#FEFBEB',
  '#F0FAFF',
  '#FFF2F9',
  '#FAF4EF',
  '#F0FCFF',
  '#FFF6FB',
  '#FCEFFF',
  '#FFF4F1',
  '#EAFAF4',
  '#EEF4FF',
  '#FFF5EC',
  '#F5FFEA',
  '#F5FAFF',
  '#F8EDE7',
  '#F4EEFF',
  '#ECFEFF',
  '#FFEDED',
  '#F2F7FF',
  '#F4FFFB',
  '#FBF8FF',
  '#FFF3EC',
  '#F0FFFC',
  '#F6F2FF',
  '#FFF0F7',
  '#EDFBFF',
  '#FFFCF0',
  '#F0FFF6',
  '#F3F2FF',
  '#FFF0F0',
  '#F7F0FF',
  '#FFF4ED',
  '#EDFFFA',
  '#FFF0FD',
  '#EFF8FF',
  '#F6FFF0',
  '#FFF0F3',
  '#FFF6F0',
  '#F4F8F9',
  '#EBFCFF',
  '#FFF2EC',
  '#F5F0FF',
  '#EDFFF5',
  '#FFF8F0',
  '#EDFAFF',
  '#FFF0F4',
  '#F3F1FF',
  '#F7FFF0',
  '#FFF5F0',
  '#F5EDFF',
  '#EDFFFC',
  '#FFEDFA',
  '#F0F5FF',
  '#FFF0F6',
  '#F0FFF4',
  '#FFF5ED',
  '#F0F3FF',
  '#FFF0F5',
  '#EDFFF6',
  '#FFEBEB',
  '#F1F0FF',
];

export const CUSTOM_BLOCKOUT_LABEL = 'Custom Blockout';
export const BLOCKOUT_DETAIL_TITLE = 'Blockout Details';
export const EVENT_DETAIL_TITLE = 'Event Details';
export const EVENT_COLOR_CODE = '#A9A9A9';
export const EVENT_BACKGROUND_COLOR_CODE = '#E0E0E3';

export const TIMEGRID_VIEW = {
  PRACTITIONER: 'practitioner',
  CHAIR: 'chair',
};

export const PMS_EVENT_TYPE = {
  OFFICE_CLOSED_HOUR: 'office_closed_hour',
  OFFICE_HOLIDAY: 'office_holiday',
  EVENT: 'event', // lunch event
  BLOCKOUT: 'blockout', // PMS blockout / custom blockout on a chair
  PMS_PRACTITIONER_HOURS: 'pms_practitioner_hours',
  AVAILABLE_EVENT: 'available_event',
  BLOCKED: 'blocked', // used for grey out cells if operatories are not assigned to doctor
  BLOCKED_DOCTOR_HOVERING_EXAM_CLEANING:
    'blocked_doctor_hovering_exam_cleaning',
};

// Event types rendered side by side when their time slots overlap on Schedule
export const SIDE_BY_SIDE_EVENT_TYPES = [
  PMS_EVENT_TYPE.PMS_PRACTITIONER_HOURS,
  PMS_EVENT_TYPE.AVAILABLE_EVENT,
  PMS_EVENT_TYPE.EVENT,
];

export const ERROR_REASON_MESSAGE = {
  [ERROR_REASON.DUPLICATE_BOOKING]: 'Duplicate appointment error.',
  [ERROR_REASON.PARTIAL_PATIENT_MATCH]: 'Partial patient match error.',
  [ERROR_REASON.OUT_OF_OFFICE_HOURS]: 'Outside office hours error.',
  [ERROR_REASON.OUT_OF_PRACTITIONER_HOURS]: 'Outside practitioner hours error.',
  [ERROR_REASON.GENERAL_ERROR]:
    'This appointment could not be written in the practice software. Please create the appointment in your practice software.',
  [ERROR_REASON.PATIENT_NEED_ADD_TO_FAMILY]:
    'Patient needs to be added to a family file.',
  [ERROR_REASON.WRITE_FAIL]: 'Failed to write to PMS.',
  [ERROR_REASON.UNFOUNDED_AVAILABLE_PRACTITIONER]:
    'Unable to find an appropriate time slot for this patient.',
  [ERROR_REASON.INACTIVE_PATIENT]: 'Inactive patient',
};

export const SERVICE_NAME = {
  EXAM_CLEANING: 'Exam & Cleaning',
};

export const BOOKING_TYPE = {
  SMART_LINK: 'smart_link',
  SIGN_IN: 'sign_in',
  NON_SIGN_IN: 'non_sign_in',
};

export const SPECIALTY_NAME = {
  HYGIENIST: 'Hygienist',
  DENTIST: 'Dentist',
  BOTH: 'Both',
};

export const EXAM_PLACEMENT = {
  HEAD: 'head',
  TAIL: 'tail',
  HEAD_SAME_CHAIR: 'head_same_chair',
  TAIL_SAME_CHAIR: 'tail_same_chair',
  DURING_FIRST_PART_DIFF: 'during_first_part_diff_chair',
  DURING_LAST_PART_DIFF: 'during_last_part_diff_chair',
};

export const MAX_HYGIENIST_DURATION = 180;

export const MAX_OTHER_SERVICES_DURATION = 180;

export const APPOINTMENT_DESCRIPTION_MAX_LENGTH = 255;

export const MAX_DOCTOR_DURATION = 90;

export const TIME_DURATION_OPTIONS = [5, 10, 15, 20, 30];

export const INTERVAL_OPTIONS_PER_UNIT_DURATION = {
  5: [5, 10, 15, 20, 30, 60],
  10: [10, 20, 30, 60],
  15: [15, 30, 60],
  20: [20, 60],
  30: [30, 60],
};

export const AGE_RANGE_GROUP = {
  firstGroup: range(4, 10),
  secondGroup: range(10, 17),
};

export const FIRST_GROUP_FROM_AGE = [0, 1, 2];

export const PMS_NAME = {
  DENTRIX: 'dentrix',
  CURVE: 'curve',
  EAGLESOFT: 'eaglesoft',
  OPENDENTAL: 'opendental',
  DENTICON: 'denticon',
  DENTRIX_ASCEND: 'dentrixascend',
  OTHER: 'other',
};

export const WRITE_STATUS = {
  PENDING: 'pending',
  REBOOK_PENDING: 'rebook-pending',
  SUCCESS: 'success',
  FAIL: 'fail',
};

export const PMS_PENDING_ERROR_MESSAGE = {
  [PMS_NAME.CURVE]:
    'This appointment hasn’t been added to your PMS just yet. It should only take about 8 minutes to complete, just make sure the Chrome extension is installed on your browser, and that you’re logged into your PMS so the appointment can be added shortly.',
  [PMS_NAME.DENTICON]:
    'This appointment hasn’t been added to your PMS just yet. It should only take about 8 minutes to complete, just make sure the Chrome extension is installed on your browser, and that you’re logged into your PMS so the appointment can be added shortly.',
  [PMS_NAME.DENTRIX_ASCEND]:
    'This appointment hasn’t been added to your PMS just yet. It should only take about 8 minutes to complete, just make sure the Chrome extension is installed on your browser, and that you’re logged into your PMS so the appointment can be added shortly.',
  [PMS_NAME.OTHER]:
    'The appointment has not yet synced with the PMS. Please verify your PMS sync status',
};

export const ERROR_LABEL = {
  [ERROR_REASON.PATIENT_NEED_ADD_TO_FAMILY]: 'Create Family File',
  [ERROR_REASON.DUPLICATE_BOOKING]: 'Duplicate Appt',
  [ERROR_REASON.PARTIAL_PATIENT_MATCH]: 'Partial Patient Match',
  [ERROR_REASON.GENERAL_ERROR]: 'General Error',
  [ERROR_REASON.OUT_OF_OFFICE_HOURS]: 'Out of Office Hours',
  [ERROR_REASON.OUT_OF_PRACTITIONER_HOURS]: 'Out of Practitioner Hours',
  [ERROR_REASON.WRITE_FAIL]: 'Fail to Write to PMS',
  [ERROR_REASON.UNFOUNDED_AVAILABLE_PRACTITIONER]: 'Misassigned Practitioner',
  [ERROR_REASON.INACTIVE_PATIENT]: 'Inactive Patient',
};

export const SLOT_SHOW_TYPE = {
  ANY_TIME_OF_DAY: '1000',
  FIRST_APPOINTMENT_OF_DAY: '0100',
  MIDDLE_APPOINTMENT_OF_DAY: '0010',
  LAST_APPOINTMENT_OF_DAY: '0001',
};

export const AMOUNT_DAYS_OF_A_MONTH = 30;

export const CANCEL_REASON_CODE = {
  INCORRECT_CONTINUING_CARE: 'incorrect-continuing-care',
  UNAVAILABLE_DOCTOR: 'unavailable-doctor',
  UNAVAILABLE_REQUESTED_TIME: 'unavailable-requested-time',
  REBOOKED_TO_ANOTHER_DATE: 'rebooked-to-another-date',
  UNRESPONSIVE_PATIENT: 'unresponsive-patient',
  UNIDEAL_UNOPTIMIZATION_APPT: 'unideal-unoptimization-appt',
  OTHER: 'other',
};

export const CANCEL_REASON_CODE_WITH_DESCRIPTION = [
  {
    code: CANCEL_REASON_CODE.INCORRECT_CONTINUING_CARE,
    description: 'Incorrect continuing care for the appointment',
    color: '#F5D7F7',
  },
  {
    code: CANCEL_REASON_CODE.UNAVAILABLE_DOCTOR,
    description: 'Provider not available',
    color: '#D78DD6',
  },
  {
    code: CANCEL_REASON_CODE.UNAVAILABLE_REQUESTED_TIME,
    description: 'Requested time no longer available',
    color: '#F8BBD0',
  },
  {
    code: CANCEL_REASON_CODE.REBOOKED_TO_ANOTHER_DATE,
    description: 'Rebooked by the office to another day/time',
    color: '#B01BAE',
  },
  {
    code: CANCEL_REASON_CODE.UNRESPONSIVE_PATIENT,
    description: 'Patient is unresponsive to text/communications',
    color: '#4F034C',
  },
  {
    code: CANCEL_REASON_CODE.UNIDEAL_UNOPTIMIZATION_APPT,
    description: 'Requested time not ideal/optimized for other appointments',
    color: '#C4B0DE',
  },
  { code: CANCEL_REASON_CODE.OTHER, description: 'Other', color: '#471692' },
];

export const OTHER_GROUP_NAME = 'Independent Practices';

export const INSURANCE_PAY_FOR_MY_SELF = 'paying_for_myself';

export const BOOKING_MODE = {
  DOCTOR_SCHEDULING: 'practitioner-scheduling',
  CHAIR_SCHEDULING: 'chair-scheduling',
};

export const sortingOrder: {
  ASC: 'ASC';
  DESC: 'DESC';
} = {
  ASC: 'ASC',
  DESC: 'DESC',
};

export const HOUR_TYPE = {
  ALL: 'all',
  OUT_OF_WORKING_HOUR: 'out_of_working_hour',
  DURING_WORKING_HOUR: 'during_working_hour',
};
